// Helpers for the mobile "Recherche étendue" (advanced search).
//
// The advanced filters map 1:1 to the desktop `workorders_filter_form[...]`
// params so the mobile full-screen search reuses the exact same backend filter
// form (WorkordersFilterForm) as the web app.
//
// This module is intentionally dependency-free (no `@redux/store` import) so it
// can be imported from reducers without creating a circular dependency.

export const ADVANCED_PARAM_KEY = 'workorders_filter_form'

// Empty advanced-filter shape. Array fields mirror the desktop `multiple: true`
// comboboxes; scalar fields mirror the single-select / text inputs.
export const ADVANCED_DEFAULTS = {
  workorder_search:   '',
  workorder_type:     [],
  status:             [],
  closed_status:      '',
  manager:            [],
  technician:         [],
  partner:            [],
  product:            [],
  agency:             '',
  workorder_priority: '',
  category:           '',
  client:             '',
  site:               '',
  material:           '',
  period_type:        '',
  period_from:        '', // ISO yyyy-mm-dd (UI); combined into `period` on serialize
  period_to:          '',
  near:               '',
  postal_code:        '',
  extra_field_answers: {}, // { [extraFieldId]: answerValue }
  applied:            false // mode flag: the advanced search was committed (even with no values)
}

// Native date input (yyyy-mm-dd) <-> desktop filter display (dd/mm/yyyy).
export const isoToDisplay = iso => {
  if (!iso) return ''
  const [y, m, d] = iso.split('-')
  return `${d}/${m}/${y}`
}
export const displayToIso = display => {
  if (!display) return ''
  const [d, m, y] = display.split('/')
  return y && m && d ? `${y}-${m}-${d}` : ''
}

const ARRAY_KEYS  = ['workorder_type', 'status', 'manager', 'technician', 'partner', 'product']
const SCALAR_KEYS = [
  'workorder_search', 'closed_status', 'agency', 'workorder_priority',
  'category', 'client', 'site', 'material', 'near', 'postal_code'
]

const isBlank = value =>
  value === '' || value === null || value === undefined ||
  (Array.isArray(value) && value.length === 0) ||
  (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0)

// Number of active filter "values" — array selections count individually,
// scalars count as one, the period counts as one (matches the design footer pill).
export const advancedActiveCount = (filters = {}) => {
  let count = 0
  ARRAY_KEYS.forEach(key => { count += (filters[key] || []).length })
  SCALAR_KEYS.forEach(key => { if (!isBlank(filters[key])) count += 1 })
  if (filters.period_type || (filters.period_from && filters.period_to)) count += 1
  count += Object.values(filters.extra_field_answers || {}).filter(v => !isBlank(v)).length
  return count
}

export const advancedActive = (filters = {}) => advancedActiveCount(filters) > 0

// Whether the advanced search was committed — engages the advanced query branch
// even when no filter values are set (so a "clean" search shows all interventions).
export const advancedApplied = (filters = {}) => !!filters.applied

// Strip blank values and shape extra_field_answers the way WorkordersFilterForm
// expects ({ id => { extra_field_id, answer } }). Defaults period_type so a date
// range without an explicit field still filters (like the desktop default).
export const compactAdvanced = (filters = {}) => {
  const out = {}

  Object.entries(filters).forEach(([key, value]) => {
    if (key === 'period_from' || key === 'period_to' || key === 'applied') return // meta / composed below
    if (key === 'extra_field_answers') {
      const answers = {}
      Object.entries(value || {}).forEach(([fieldId, answer]) => {
        if (!isBlank(answer)) answers[fieldId] = { extra_field_id: fieldId, answer }
      })
      if (Object.keys(answers).length) out.extra_field_answers = answers
    } else if (!isBlank(value)) {
      out[key] = value
    }
  })

  // Compose the date range the way the desktop WorkordersFilterForm expects
  // ("dd/mm/yyyy to dd/mm/yyyy"), defaulting the date field if none was chosen.
  if (filters.period_from && filters.period_to) {
    out.period = `${isoToDisplay(filters.period_from)} to ${isoToDisplay(filters.period_to)}`
    if (!out.period_type) out.period_type = 'date_planned'
  }

  return out
}

// Rebuild the advanced-filter object from the current URL so applied filters
// survive a page reload (the app persists quick-search state via the URL).
export const parseAdvancedFromSearch = (search = window.location.search) => {
  const filters = { ...ADVANCED_DEFAULTS, extra_field_answers: {} }
  const params  = new URLSearchParams(search)

  for (const [rawKey, value] of params.entries()) {
    const match = rawKey.match(/^workorders_filter_form\[([^\]]+)\](.*)$/)
    if (!match) continue

    const key  = match[1]
    const rest = match[2]

    if (key === 'period' && rest === '') {
      const [from, to] = value.split(' to ')
      filters.period_from = displayToIso(from || '')
      filters.period_to   = displayToIso(to || '')
    } else if (key === 'extra_field_answers') {
      const nested = rest.match(/^\[([^\]]+)\]\[([^\]]+)\]$/)
      if (nested && nested[2] === 'answer') {
        filters.extra_field_answers = { ...filters.extra_field_answers, [nested[1]]: value }
      }
    } else if (rest === '[]') {
      filters[key] = [...(filters[key] || []), value]
    } else if (rest === '') {
      filters[key] = value
    }
  }

  return filters
}
